import React, { useEffect, useState, createElement } from 'react';
import { ArrowRightIcon, CheckIcon, XIcon, UserIcon, BuildingIcon, TrendingUpIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { FinalCtaSection } from '../components/FinalCtaSection';
export function WebsiteLatenMakenOfZelfBouwenPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Website laten maken of zelf bouwen? | Noord';
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'Website laten maken of zelf bouwen? Vergelijk de voor- en nadelen van uitbesteden vs DIY. Ontdek welke optie het beste past bij jouw bedrijf en budget.';
    // Open Graph meta tags
    const ogTags: Record<string, string> = {
      'og:title': 'Website laten maken of zelf bouwen? | Noord',
      'og:description': 'Website laten maken of zelf bouwen? Vergelijk de voor- en nadelen van uitbesteden vs DIY.',
      'og:type': 'article',
      'og:url': 'https://noorddigital.nl/website-laten-maken-of-zelf-bouwen',
      'og:site_name': 'Noord Digital',
      'og:locale': 'nl_NL'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, []);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const scrollToContact = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('contact')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const faqs = [{
    q: 'Is een website builder goed genoeg voor mijn bedrijf?',
    a: 'Voor een eenvoudige online aanwezigheid kan een website builder voldoende zijn. Maar als je serieus wilt groeien, meer klanten wilt aantrekken via Google en een professionele uitstraling belangrijk vindt, dan is een op maat gemaakte website de betere investering.'
  }, {
    q: 'Wat kost een webdesigner?',
    a: 'Een professionele bedrijfswebsite kost gemiddeld tussen de €1.500 en €3.500. Dit is inclusief design, ontwikkeling, SEO-basis en contentplaatsing. Bij Noord Digital werken we met transparante prijzen zonder verborgen kosten.'
  }, {
    q: 'Kan ik later overstappen van een website builder naar WordPress?',
    a: 'Ja, dat kan. Houd er wel rekening mee dat content en design opnieuw moeten worden opgebouwd. Het is vaak voordeliger om direct met de juiste oplossing te starten dan later over te stappen.'
  }];
  return <main className="pt-16 lg:pt-[72px]" data-id="element-2323">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-2324">
        <div className="absolute inset-0 pointer-events-none" data-id="element-2325">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-2326" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-2327" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-2328" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2329">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-sm mb-8 animate-fade-in-up" data-id="element-2330">
            <a href="#/" className="text-navy-400 hover:text-white transition-colors" data-id="element-2331">
              Home
            </a>
            <span className="text-navy-500" data-id="element-2332">/</span>
            <a href="#/kennisbank" className="text-navy-400 hover:text-white transition-colors" data-id="element-2333">
              Kennisbank
            </a>
            <span className="text-navy-500" data-id="element-2334">/</span>
            <span className="text-accent-light font-medium" data-id="element-2335">
              Laten maken of zelf bouwen
            </span>
          </nav>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up" data-id="element-2336">
            Website laten maken of zelf bouwen?
          </h1>
          <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mx-auto mb-10 animate-fade-in-up-delay-1" data-id="element-2337">
            De voor- en nadelen van beide opties op een rij. Ontdek welke keuze
            het beste past bij jouw situatie.
          </p>
          <div className="animate-fade-in-up-delay-2" data-id="element-2338">
            <a href="#contact" onClick={scrollToContact} className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-lg" data-id="element-2339">
              Vraag advies
              <ArrowRightIcon className="w-5 h-5" data-id="element-2340" />
            </a>
          </div>
        </div>
      </section>

      {/* Zelf een website maken */}
      <section className="py-24 bg-surface-muted" data-id="element-2341">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2342">
          <div className="text-center mb-16" data-id="element-2343">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2344">
              Zelf een website maken
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-2345">
              Met een website builder zoals Wix of Squarespace kun je zelf aan
              de slag. Dit zijn de voor- en nadelen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12" data-id="element-2346">
            {/* Voordelen */}
            <div data-id="element-2347">
              <h3 className="text-xl font-bold text-navy-900 mb-6 flex items-center gap-2" data-id="element-2348">
                <span className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600" data-id="element-2349">
                  <CheckIcon className="w-5 h-5" data-id="element-2350" />
                </span>
                Voordelen
              </h3>
              <div className="space-y-4" data-id="element-2351">
                <div className="bg-white rounded-card shadow-soft p-6" data-id="element-2352">
                  <h4 className="font-bold text-navy-900 mb-2 flex items-center gap-2" data-id="element-2353">
                    <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-2354" />
                    Goedkoper
                  </h4>
                  <p className="text-slate-600 text-sm leading-relaxed" data-id="element-2355">
                    Website builders starten al vanaf €10 per maand. Ideaal als
                    je een beperkt budget hebt.
                  </p>
                </div>
                <div className="bg-white rounded-card shadow-soft p-6" data-id="element-2356">
                  <h4 className="font-bold text-navy-900 mb-2 flex items-center gap-2" data-id="element-2357">
                    <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-2358" />
                    Snelle start
                  </h4>
                  <p className="text-slate-600 text-sm leading-relaxed" data-id="element-2359">
                    Binnen een paar uur heb je een werkende website online met
                    kant-en-klare templates.
                  </p>
                </div>
                <div className="bg-white rounded-card shadow-soft p-6" data-id="element-2360">
                  <h4 className="font-bold text-navy-900 mb-2 flex items-center gap-2" data-id="element-2361">
                    <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-2362" />
                    Veel templates
                  </h4>
                  <p className="text-slate-600 text-sm leading-relaxed" data-id="element-2363">
                    Kies uit honderden templates en pas ze aan naar jouw smaak
                    zonder technische kennis.
                  </p>
                </div>
              </div>
            </div>

            {/* Nadelen */}
            <div data-id="element-2364">
              <h3 className="text-xl font-bold text-navy-900 mb-6 flex items-center gap-2" data-id="element-2365">
                <span className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center text-red-600" data-id="element-2366">
                  <XIcon className="w-5 h-5" data-id="element-2367" />
                </span>
                Nadelen
              </h3>
              <div className="space-y-4" data-id="element-2368">
                <div className="bg-white rounded-card shadow-soft p-6" data-id="element-2369">
                  <h4 className="font-bold text-navy-900 mb-2 flex items-center gap-2" data-id="element-2370">
                    <XIcon className="w-5 h-5 text-red-500 flex-shrink-0" data-id="element-2371" />
                    Beperkte flexibiliteit
                  </h4>
                  <p className="text-slate-600 text-sm leading-relaxed" data-id="element-2372">
                    Je zit vast aan de mogelijkheden van het platform. Maatwerk
                    is vaak niet mogelijk.
                  </p>
                </div>
                <div className="bg-white rounded-card shadow-soft p-6" data-id="element-2373">
                  <h4 className="font-bold text-navy-900 mb-2 flex items-center gap-2" data-id="element-2374">
                    <XIcon className="w-5 h-5 text-red-500 flex-shrink-0" data-id="element-2375" />
                    SEO vaak zwakker
                  </h4>
                  <p className="text-slate-600 text-sm leading-relaxed" data-id="element-2376">
                    Website builders bieden beperkte SEO-opties, waardoor je
                    minder goed vindbaar bent in Google.
                  </p>
                </div>
                <div className="bg-white rounded-card shadow-soft p-6" data-id="element-2377">
                  <h4 className="font-bold text-navy-900 mb-2 flex items-center gap-2" data-id="element-2378">
                    <XIcon className="w-5 h-5 text-red-500 flex-shrink-0" data-id="element-2379" />
                    Technische kennis nodig
                  </h4>
                  <p className="text-slate-600 text-sm leading-relaxed" data-id="element-2380">
                    Ondanks de eenvoud loop je al snel tegen technische
                    beperkingen aan bij aanpassingen.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Website laten maken */}
      <section className="py-24 bg-white" data-id="element-2381">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2382">
          <div className="text-center mb-16" data-id="element-2383">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2384">
              Website laten maken door een professional
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-2385">
              Een webdesigner bouwt een website die volledig is afgestemd op
              jouw bedrijf en doelen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2386">
            <div className="bg-surface-muted p-8 rounded-card border border-navy-100" data-id="element-2387">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2388">
                <CheckIcon className="w-6 h-6 text-accent" data-id="element-2389" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2390">
                Professioneel design
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2391">
                Een uniek ontwerp dat past bij jouw merk en vertrouwen wekt bij
                bezoekers. Geen standaard template.
              </p>
            </div>

            <div className="bg-surface-muted p-8 rounded-card border border-navy-100" data-id="element-2392">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2393">
                <CheckIcon className="w-6 h-6 text-accent" data-id="element-2394" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2395">
                SEO-optimalisatie
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2396">
                Technische SEO, snelheid en contentoptimalisatie zorgen ervoor
                dat je beter gevonden wordt in Google.
              </p>
            </div>

            <div className="bg-surface-muted p-8 rounded-card border border-navy-100" data-id="element-2397">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2398">
                <CheckIcon className="w-6 h-6 text-accent" data-id="element-2399" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2400">
                Volledig maatwerk
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2401">
                Van functionaliteiten tot koppelingen: alles wordt gebouwd op
                basis van jouw specifieke wensen en doelen.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Wanneer kies je wat */}
      <section className="py-24 bg-navy-900 relative overflow-hidden" data-id="element-2402">
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }} data-id="element-2403" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2404">
          <div className="text-center mb-16" data-id="element-2405">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-2406">
              Wanneer kies je wat?
            </h2>
            <p className="text-lg text-navy-300 max-w-2xl mx-auto" data-id="element-2407">
              De juiste keuze hangt af van jouw situatie, budget en ambities.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2408">
            <div className="bg-white/[0.06] border border-white/10 rounded-2xl p-8" data-id="element-2409">
              <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mb-6" data-id="element-2410">
                <UserIcon className="w-6 h-6 text-white" data-id="element-2411" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3" data-id="element-2412">ZZP'er</h3>
              <p className="text-navy-300 leading-relaxed" data-id="element-2413">
                Als zzp'er met een beperkt budget kan een website builder een
                goede start zijn. Wil je groeien en meer klanten aantrekken? Dan
                loont een professionele website zich snel.
              </p>
            </div>

            <div className="bg-white/[0.06] border border-white/10 rounded-2xl p-8" data-id="element-2414">
              <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mb-6" data-id="element-2415">
                <BuildingIcon className="w-6 h-6 text-white" data-id="element-2416" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3" data-id="element-2417">MKB</h3>
              <p className="text-navy-300 leading-relaxed" data-id="element-2418">
                Voor mkb-bedrijven is een professionele website vrijwel altijd
                de betere keuze. Je hebt meer pagina's nodig, betere SEO en een
                betrouwbare uitstraling die past bij je merk.
              </p>
            </div>

            <div className="bg-white/[0.06] border border-white/10 rounded-2xl p-8" data-id="element-2419">
              <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mb-6" data-id="element-2420">
                <TrendingUpIcon className="w-6 h-6 text-white" data-id="element-2421" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3" data-id="element-2422">
                Groeiend bedrijf
              </h3>
              <p className="text-navy-300 leading-relaxed" data-id="element-2423">
                Groeit je bedrijf? Dan heb je een schaalbare website nodig met
                maatwerk, koppelingen en doorlopend onderhoud. Een website
                builder schiet dan tekort.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Gerelateerde pagina's */}
      <section className="py-16 bg-white border-b border-navy-100" data-id="element-2424">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2425">
          <h3 className="text-lg font-semibold text-navy-900 mb-6" data-id="element-2426">
            Ontdek meer:
          </h3>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-2427">
            <a href="#/website-laten-maken" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2428">
              Website laten maken
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2429">•</span>
            <a href="#/wordpress-websites" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2430">
              WordPress websites
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2431">•</span>
            <a href="#/kosten-website-laten-maken" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2432">
              Kosten website laten maken
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-surface-muted" data-id="element-2433">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2434">
          <div className="text-center mb-16" data-id="element-2435">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2436">
              Veelgestelde vragen
            </h2>
          </div>

          <div className="space-y-4" data-id="element-2437">
            {faqs.map((faq, index) => <div key={index} className="bg-white rounded-xl shadow-soft overflow-hidden" data-id="element-2438">
                <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-2439">
                  <span className="font-bold text-navy-900" data-id="element-2440">{faq.q}</span>
                  {openFaq === index ? <ChevronUpIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-2441" /> : <ChevronDownIcon className="w-5 h-5 text-navy-400 flex-shrink-0" data-id="element-2442" />}
                </button>
                {openFaq === index && <div className="px-6 pb-5 text-slate-600 leading-relaxed" data-id="element-2443">
                    {faq.a}
                  </div>}
              </div>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-2444" />
    </main>;
}